// Specimen plate (DESIGN_SPEC §10.14): double hairline frame, "· + · ✦" gutter bands (SVG masks), plate fill, square.
//   <Plate sm? live? onActivate? label? drag? anno? className? {...div attrs}>{children}</Plate>
//       live → the frame is ruled in live cyan (the running guest screen, the session replay)
//       onActivate → role=button + tabIndex 0; click, Enter and Space call it (the Submit drop target)
//       drag → dashed inner frame while a file is dragged; anno → labels in the right gutter at y% (replaces the right band)
//   <Specimen label? className?>{plate}{museum}</Specimen>   figure.specimen: the inline-size container the ASCII scales against
//   <Museum no lbl more? />                                  figcaption: "SPECIMEN 0001" + a short mono label + "more" line
//   <PlateCap>1280 × 800 · frame 1.0 s ago · view only</PlateCap>   mono caption under a plate
// Only the Submit specimen, guest screens, stored frames and empty-state moths get a plate.
import type { HTMLAttributes, KeyboardEvent, ReactNode } from "react";
import { cx, vars } from "./cx";

type PlateProps = Omit<HTMLAttributes<HTMLDivElement>, "onClick"> & {
  sm?: boolean;
  live?: boolean;
  onActivate?: () => void;
  label?: string;
  drag?: boolean;
  anno?: readonly { label: string; y: number }[];
};

export function Plate({ sm, live, onActivate, label, drag, anno, className, children, onKeyDown, ...rest }: PlateProps) {
  const button = !!onActivate;
  const keyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(e);
    if (!button || e.defaultPrevented || e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onActivate?.();
    }
  };
  return (
    <div
      className={cx("plate", sm && "plate--sm", live && "plate--live", className)}
      role={button ? "button" : undefined}
      tabIndex={button ? 0 : undefined}
      aria-label={button ? label : undefined}
      data-drag={drag ? "true" : undefined}
      onClick={button ? () => onActivate?.() : undefined}
      onKeyDown={keyDown}
      {...rest}
    >
      <span className="plate-edge plate-edge--t" aria-hidden="true" />
      <span className="plate-edge plate-edge--b" aria-hidden="true" />
      <span className="plate-edge plate-edge--l" aria-hidden="true" />
      {anno?.length ? (
        <span className="plate-anno" aria-hidden="true">
          {anno.map((a) => (
            <span key={`${a.label}-${a.y}`} style={vars({ "--y": a.y })}>
              {a.label}
            </span>
          ))}
        </span>
      ) : (
        <span className="plate-edge plate-edge--r" aria-hidden="true" />
      )}
      <div className="plate-field">{children}</div>
    </div>
  );
}

export function Specimen({ label, className, children }: { label?: string; className?: string; children: ReactNode }) {
  return (
    <figure className={cx("specimen", className)} aria-label={label}>
      {children}
    </figure>
  );
}

export function Museum({ no, lbl, more }: { no: ReactNode; lbl: ReactNode; more?: ReactNode }) {
  return (
    <figcaption className="museum">
      <span className="no">{no}</span>
      <span className="lbl">{lbl}</span>
      {more && <span className="more">{more}</span>}
    </figcaption>
  );
}

export function PlateCap({ className, children }: { className?: string; children: ReactNode }) {
  return <p className={cx("plate-cap", className)}>{children}</p>;
}
